import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Syringe, CalendarDays, BarChart3, Users, Plus, Trash2, AlertTriangle,
  Clock, ChevronDown, Activity, TrendingUp, Shield, Eye, EyeOff, ArrowRight,
  CheckCircle2, X, Sparkles, RefreshCw, Lightbulb, Zap, ShieldAlert, Mail, Send,
  Flame, FlaskConical, Trophy, Lock
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell
} from 'recharts';
import apiClient from '../lib/api';
import { useAuth } from '../lib/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  { id: 'log', label: 'Log Dose', icon: Syringe },
  { id: 'timeline', label: 'Timeline', icon: CalendarDays },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'community', label: 'Community', icon: Users },
];

const SEVERITY_LABELS = ['', 'Mild', 'Noticeable', 'Moderate', 'Strong', 'Severe'];
const SEVERITY_COLORS = ['', '#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444'];

function LogDoseTab({ token, options, onRefresh }) {
  const [peptide, setPeptide] = useState('');
  const [dose, setDose] = useState('');
  const [unit, setUnit] = useState('mcg');
  const [site, setSite] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('morning');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [showSideEffect, setShowSideEffect] = useState(false);
  const [seSymptom, setSeSymptom] = useState('');
  const [seSeverity, setSeSeverity] = useState(2);
  const [seNotes, setSeNotes] = useState('');

  const handleLogDose = async (e) => {
    e.preventDefault();
    if (!peptide || !dose) return;
    setSaving(true);
    try {
      await apiClient.post('/protocol/logs', {
        peptide, dose_amount: parseFloat(dose), dose_unit: unit,
        injection_site: site, time_of_day: timeOfDay, notes, date,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Dose logged');
      setDose(''); setNotes('');
      onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setSuccess(''); }
    setSaving(false);
  };

  const handleLogSideEffect = async (e) => {
    e.preventDefault();
    if (!seSymptom) return;
    setSaving(true);
    try {
      await apiClient.post('/protocol/side-effects', {
        peptide, symptom: seSymptom, severity: seSeverity, notes: seNotes, date,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Side effect logged');
      setSeSymptom(''); setSeNotes(''); setSeSeverity(2); setShowSideEffect(false);
      onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setSuccess(''); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="log-dose-tab">
      {success && (
        <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm" data-testid="log-success-msg">
          <CheckCircle2 className="w-4 h-4" /> {success}
        </div>
      )}

      <form onSubmit={handleLogDose} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Peptide *</label>
            <select value={peptide} onChange={e => setPeptide(e.target.value)} required
              className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#00F0FF]/50 focus:outline-none transition"
              data-testid="log-peptide-select">
              <option value="">Select peptide...</option>
              {(options?.peptides || []).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Dose *</label>
              <input type="number" step="any" value={dose} onChange={e => setDose(e.target.value)} required placeholder="250"
                className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#00F0FF]/50 focus:outline-none transition"
                data-testid="log-dose-input" />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Unit</label>
              <select value={unit} onChange={e => setUnit(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#00F0FF]/50 focus:outline-none transition"
                data-testid="log-unit-select">
                <option value="mcg">mcg</option>
                <option value="mg">mg</option>
                <option value="IU">IU</option>
                <option value="mL">mL</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Injection Site</label>
            <select value={site} onChange={e => setSite(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#00F0FF]/50 focus:outline-none transition"
              data-testid="log-site-select">
              <option value="">Select site...</option>
              {(options?.injection_sites || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Time of Day</label>
            <select value={timeOfDay} onChange={e => setTimeOfDay(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#00F0FF]/50 focus:outline-none transition"
              data-testid="log-time-select">
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
              <option value="night">Before Bed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#00F0FF]/50 focus:outline-none transition"
              data-testid="log-date-input" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Notes</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..."
              className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#00F0FF]/50 focus:outline-none transition"
              data-testid="log-notes-input" />
          </div>
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={saving || !peptide || !dose}
            className="px-6 py-2.5 bg-[#00F0FF] text-black font-bold text-sm uppercase tracking-wider hover:bg-[#00d6e0] disabled:opacity-40 transition"
            data-testid="log-dose-submit">
            {saving ? 'Saving...' : 'Log Dose'}
          </button>
          <button type="button" onClick={() => setShowSideEffect(!showSideEffect)}
            className="px-4 py-2.5 border border-zinc-700 text-zinc-400 text-sm flex items-center gap-2 hover:border-amber-500/50 hover:text-amber-400 transition"
            data-testid="log-side-effect-toggle">
            <AlertTriangle className="w-4 h-4" /> Log Side Effect
          </button>
        </div>
      </form>

      {showSideEffect && (
        <form onSubmit={handleLogSideEffect} className="p-5 bg-amber-500/5 border border-amber-500/20 space-y-4" data-testid="side-effect-form">
          <div className="flex items-center justify-between">
            <h4 className="text-amber-400 font-bold text-sm uppercase tracking-wider">Log Side Effect</h4>
            <button type="button" onClick={() => setShowSideEffect(false)} className="text-zinc-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Symptom *</label>
              <select value={seSymptom} onChange={e => setSeSymptom(e.target.value)} required
                className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-amber-500/50 focus:outline-none transition"
                data-testid="se-symptom-select">
                <option value="">Select symptom...</option>
                {(options?.side_effects || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">
                Severity: <span style={{ color: SEVERITY_COLORS[seSeverity] }}>{SEVERITY_LABELS[seSeverity]}</span>
              </label>
              <input type="range" min="1" max="5" value={seSeverity} onChange={e => setSeSeverity(parseInt(e.target.value))}
                className="w-full accent-amber-500" data-testid="se-severity-range" />
              <div className="flex justify-between text-[9px] text-zinc-600 mt-1">
                <span>Mild</span><span>Severe</span>
              </div>
            </div>
            <div className="sm:col-span-2">
              <input type="text" value={seNotes} onChange={e => setSeNotes(e.target.value)} placeholder="Describe the side effect..."
                className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-amber-500/50 focus:outline-none transition"
                data-testid="se-notes-input" />
            </div>
          </div>
          <button type="submit" disabled={saving || !seSymptom}
            className="px-5 py-2 bg-amber-500 text-black font-bold text-sm uppercase tracking-wider hover:bg-amber-400 disabled:opacity-40 transition"
            data-testid="se-submit-btn">
            Log Side Effect
          </button>
          <p className="text-xs text-zinc-600 mt-1">
            Learn about common reactions in our <Link to="/blogs/article/peptide-side-effects-what-the-research-actually-says" className="text-[#00F0FF] hover:underline">Peptide Side Effects Guide</Link>
          </p>
        </form>
      )}
    </div>
  );
}

function TimelineTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [effects, setEffects] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, effectsRes] = await Promise.all([
        apiClient.get(`/protocol/logs?days=${days}`, { headers: { Authorization: `Bearer ${token}` } }),
        apiClient.get(`/protocol/side-effects?days=${days}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setLogs(logsRes.data || []);
      setEffects(effectsRes.data || []);
    } catch (e) { console.warn('Protocol logs fetch failed:', e); }
    setLoading(false);
  }, [token, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeleteLog = async (logId) => {
    await apiClient.delete(`/protocol/logs/${logId}`, { headers: { Authorization: `Bearer ${token}` } });
    fetchData();
  };
  const handleDeleteEffect = async (entryId) => {
    await apiClient.delete(`/protocol/side-effects/${entryId}`, { headers: { Authorization: `Bearer ${token}` } });
    fetchData();
  };

  // Group by date
  const grouped = {};
  logs.forEach(l => { (grouped[l.date] = grouped[l.date] || { doses: [], effects: [] }).doses.push(l); });
  effects.forEach(e => { (grouped[e.date] = grouped[e.date] || { doses: [], effects: [] }).effects.push(e); });
  const sortedDates = Object.keys(grouped).sort().reverse();

  return (
    <div data-testid="timeline-tab">
      <div className="flex items-center gap-3 mb-6">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">Period:</span>
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1 text-xs font-mono ${days === d ? 'bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30' : 'text-zinc-500 border-zinc-800 hover:text-white'} border transition`}
            data-testid={`timeline-period-${d}`}>{d}d</button>
        ))}
      </div>

      {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
      {loading ? (
        <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-zinc-900/50 animate-pulse" />)}</div>
      ) : sortedDates.length === 0 ? (
        <p className="text-zinc-600 text-sm py-8 text-center">No logs yet. Start by logging a dose!</p>
      ) : (
        <div className="space-y-1">
          {sortedDates.map(date => (
            <div key={date} className="border-l-2 border-zinc-800 pl-5 pb-6 relative" data-testid={`timeline-date-${date}`}>
              <div className="absolute -left-[7px] top-0 w-3 h-3 rounded-full bg-zinc-800 border-2 border-zinc-600" />
              <p className="text-xs text-zinc-500 font-mono mb-2">{new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
              {grouped[date].doses.map(log => (
                <div key={log.log_id} className="flex items-center justify-between p-3 bg-zinc-900/30 border border-zinc-800/50 mb-1.5 group" data-testid={`timeline-log-${log.log_id}`}>
                  <div className="flex items-center gap-3">
                    <Syringe className="w-4 h-4 text-[#00F0FF]" />
                    <div>
                      <span className="text-white text-sm font-medium">{log.peptide}</span>
                      <span className="text-zinc-500 text-sm ml-2">{log.dose_amount} {log.dose_unit}</span>
                    </div>
                    {log.injection_site && <span className="text-[10px] text-zinc-600 bg-zinc-800/50 px-2 py-0.5">{log.injection_site}</span>}
                    {log.time_of_day && <span className="text-[10px] text-zinc-600 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{log.time_of_day}</span>}
                  </div>
                  <button onClick={() => handleDeleteLog(log.log_id)} className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition" data-testid={`delete-log-${log.log_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {grouped[date].effects.map(eff => (
                <div key={eff.entry_id} className="flex items-center justify-between p-3 bg-amber-500/5 border border-amber-500/10 mb-1.5 group" data-testid={`timeline-effect-${eff.entry_id}`}>
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <div>
                      <span className="text-amber-400 text-sm font-medium">{eff.symptom}</span>
                      {eff.peptide && <span className="text-zinc-500 text-xs ml-2">({eff.peptide})</span>}
                    </div>
                    <span className="text-[10px] px-2 py-0.5 border" style={{
                      color: SEVERITY_COLORS[eff.severity] || '#888',
                      borderColor: (SEVERITY_COLORS[eff.severity] || '#888') + '40'
                    }}>{SEVERITY_LABELS[eff.severity] || 'Unknown'}</span>
                  </div>
                  <button onClick={() => handleDeleteEffect(eff.entry_id)} className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const BADGE_ICONS = { flame: Flame, syringe: Syringe, alert: AlertTriangle, flask: FlaskConical, users: Users };
const TIER_STYLES = {
  bronze: { bg: 'rgba(205,127,50,0.08)', border: 'rgba(205,127,50,0.25)', text: '#cd7f32', glow: '0 0 12px rgba(205,127,50,0.3)' },
  silver: { bg: 'rgba(192,192,192,0.08)', border: 'rgba(192,192,192,0.25)', text: '#c0c0c0', glow: '0 0 12px rgba(192,192,192,0.3)' },
  gold: { bg: 'rgba(255,215,0,0.08)', border: 'rgba(255,215,0,0.25)', text: '#ffd700', glow: '0 0 12px rgba(255,215,0,0.3)' },
  platinum: { bg: 'rgba(0,240,255,0.08)', border: 'rgba(0,240,255,0.25)', text: '#00F0FF', glow: '0 0 16px rgba(0,240,255,0.4)' },
};

function StreaksAndAchievements({ token }) {
  const [streaks, setStreaks] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [sRes, aRes] = await Promise.all([
          apiClient.get('/protocol/streaks', { headers: { Authorization: `Bearer ${token}` } }),
          apiClient.get('/protocol/achievements', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setStreaks(sRes.data);
        setAchievements(aRes.data);
      } catch (e) { console.warn('Streaks/achievements fetch failed:', e); }
      setLoading(false);
    };
    fetchAll();
  }, [token]);

  if (loading) return <div className="h-28 bg-zinc-900/50 animate-pulse" data-testid="streaks-loading" />;
  if (!streaks || streaks.total_doses === 0) return null;

  const earned = (achievements?.achievements || []).filter(a => a.earned);
  const locked = (achievements?.achievements || []).filter(a => !a.earned);
  const displayLocked = expanded ? locked : locked.slice(0, 4);

  return (
    <div data-testid="streaks-achievements-panel">
      {/* Streak Header */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Flame className={`w-6 h-6 ${streaks.current_streak > 0 ? 'text-orange-400' : 'text-zinc-700'}`} />
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className={`text-3xl font-heading ${streaks.current_streak > 0 ? 'text-orange-400' : 'text-zinc-600'}`} data-testid="current-streak">
                {streaks.current_streak}
              </span>
              <span className="text-xs text-zinc-500 uppercase tracking-wider">day streak</span>
            </div>
          </div>
        </div>
        <div className="h-8 w-px bg-zinc-800" />
        <div className="flex gap-4">
          {[
            { label: 'Best', value: streaks.longest_streak, suffix: 'd' },
            { label: 'Total', value: streaks.total_doses, suffix: '' },
            { label: 'Days', value: streaks.total_days, suffix: '' },
          ].map(s => (
            <div key={s.label} className="text-center">
              <p className="text-lg font-heading text-white">{s.value}{s.suffix}</p>
              <p className="text-[9px] text-zinc-600 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
        {achievements && (
          <>
            <div className="h-8 w-px bg-zinc-800" />
            <div className="flex items-center gap-1.5">
              <Trophy className="w-4 h-4 text-[#ffd700]" />
              <span className="text-sm text-white font-medium" data-testid="earned-count">{achievements.earned_count}</span>
              <span className="text-xs text-zinc-600">/ {achievements.total_count}</span>
            </div>
          </>
        )}
      </div>

      {/* Earned Badges */}
      {earned.length > 0 && (
        <div className="mb-4">
          <h4 className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2">Earned</h4>
          <div className="flex flex-wrap gap-2">
            {earned.map(a => {
              const Icon = BADGE_ICONS[a.icon] || Trophy;
              const tier = TIER_STYLES[a.tier] || TIER_STYLES.bronze;
              return (
                <div key={a.id} className="group relative flex items-center gap-2 px-3 py-2 border transition-all"
                  style={{ background: tier.bg, borderColor: tier.border, boxShadow: tier.glow }}
                  data-testid={`badge-${a.id}`}>
                  <Icon className="w-3.5 h-3.5" style={{ color: tier.text }} />
                  <span className="text-xs font-medium" style={{ color: tier.text }}>{a.title}</span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-zinc-900 border border-zinc-700 text-zinc-300 text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    {a.desc}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Locked Badges (Progress) */}
      {locked.length > 0 && (
        <div>
          <h4 className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2">In Progress</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {displayLocked.map(a => {
              const Icon = BADGE_ICONS[a.icon] || Trophy;
              return (
                <div key={a.id} className="flex items-center gap-3 p-3 bg-zinc-900/20 border border-zinc-800/40 opacity-60 hover:opacity-80 transition"
                  data-testid={`badge-locked-${a.id}`}>
                  <div className="w-8 h-8 bg-zinc-800/50 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-3.5 h-3.5 text-zinc-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-400">{a.title}</span>
                      <span className="text-[9px] text-zinc-600">{a.current}/{a.threshold}</span>
                    </div>
                    <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-zinc-600 rounded-full transition-all" style={{ width: `${a.progress}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {locked.length > 4 && (
            <button onClick={() => setExpanded(!expanded)}
              className="mt-2 text-[10px] text-zinc-500 hover:text-[#00F0FF] transition uppercase tracking-wider"
              data-testid="achievements-expand-btn">
              {expanded ? 'Show Less' : `Show All (${locked.length - 4} more)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const INSIGHT_ICONS = {
  pattern: TrendingUp,
  optimization: Lightbulb,
  warning: ShieldAlert,
  positive: CheckCircle2,
};
const INSIGHT_COLORS = {
  pattern: { text: '#00F0FF', bg: 'rgba(0,240,255,0.06)', border: 'rgba(0,240,255,0.15)' },
  optimization: { text: '#a855f7', bg: 'rgba(168,85,247,0.06)', border: 'rgba(168,85,247,0.15)' },
  warning: { text: '#f59e0b', bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.15)' },
  positive: { text: '#22c55e', bg: 'rgba(34,197,94,0.06)', border: 'rgba(34,197,94,0.15)' },
};
const CONFIDENCE_BADGE = { high: 'text-emerald-400 border-emerald-500/20', medium: 'text-zinc-400 border-zinc-600', low: 'text-zinc-500 border-zinc-700' };

function AIInsightsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsights = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) {
      setRefreshing(true);
      try { await apiClient.post('/protocol/ai-insights/refresh', {}, { headers: { Authorization: `Bearer ${token}` } }); } catch (e) { console.warn('AI insights refresh failed:', e); }
    } else {
      setLoading(true);
    }
    try {
      const res = await apiClient.get('/protocol/ai-insights', { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.warn('AI insights fetch failed:', e); }
    setLoading(false);
    setRefreshing(false);
  }, [token]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  if (loading) return (
    <div className="p-6 bg-zinc-900/30 border border-zinc-800/50 animate-pulse" data-testid="ai-insights-loading">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-[#00F0FF]" />
        <span className="text-xs text-zinc-500 uppercase tracking-wider">Analyzing your protocol...</span>
      </div>
      <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-zinc-800/30 rounded" />)}</div>
    </div>
  );

  if (!data || data.min_data) return (
    <div className="p-5 bg-zinc-900/20 border border-dashed border-zinc-800 text-center" data-testid="ai-insights-min-data">
      <Sparkles className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
      <p className="text-zinc-500 text-sm">{data?.message || 'Log more entries to unlock AI-powered insights.'}</p>
    </div>
  );

  if (data.error) return (
    <div className="p-5 bg-zinc-900/20 border border-zinc-800 text-center" data-testid="ai-insights-error">
      <p className="text-zinc-500 text-sm">{data.error}</p>
      <button onClick={() => fetchInsights(true)} className="mt-2 text-xs text-[#00F0FF] hover:underline">Retry</button>
    </div>
  );

  const insights = data.insights || [];
  if (insights.length === 0) return null;

  return (
    <div data-testid="ai-insights-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#00F0FF]" />
          <h4 className="text-xs text-zinc-500 uppercase tracking-wider">AI Protocol Insights</h4>
          <span className="text-[9px] px-1.5 py-0.5 bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20 uppercase tracking-widest font-bold">GPT</span>
        </div>
        <div className="flex items-center gap-3">
          {data.generated_at && (
            <span className="text-[10px] text-zinc-600">
              {new Date(data.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button onClick={() => fetchInsights(true)} disabled={refreshing}
            className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-[#00F0FF] transition disabled:opacity-40"
            data-testid="ai-insights-refresh">
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Analyzing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {data.data_summary && (
        <p className="text-[10px] text-zinc-600 mb-3">
          Based on {data.data_summary.logs_analyzed} doses and {data.data_summary.effects_analyzed} side effect reports over {data.data_summary.period_days} days
        </p>
      )}

      <div className="space-y-2">
        {insights.map((insight, i) => {
          const Icon = INSIGHT_ICONS[insight.type] || Zap;
          const colors = INSIGHT_COLORS[insight.type] || INSIGHT_COLORS.pattern;
          const confClass = CONFIDENCE_BADGE[insight.confidence] || CONFIDENCE_BADGE.medium;
          return (
            <div key={insight.title || insight.detail} className="p-4 border transition-colors hover:border-opacity-60"
              style={{ background: colors.bg, borderColor: colors.border }}
              data-testid={`ai-insight-${i}`}>
              <div className="flex items-start gap-3">
                <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.text }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white text-sm font-medium">{insight.title}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 border uppercase tracking-wider ${confClass}`}>
                      {insight.confidence}
                    </span>
                  </div>
                  <p className="text-zinc-400 text-xs leading-relaxed">{insight.detail}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[9px] text-zinc-700 mt-3 flex items-center gap-1">
        <Shield className="w-2.5 h-2.5" /> Data-driven observations only. Not medical advice. Consult a healthcare professional.
      </p>
    </div>
  );
}

function DashboardTab({ token }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/protocol/dashboard?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch (e) { console.warn('Protocol dashboard fetch failed:', e); }
      setLoading(false);
    };
    fetch();
  }, [token, days]);

  if (loading) return <div className="h-40 bg-zinc-900/50 animate-pulse" />;
  if (!data || data.total_logs === 0) return (
    <div className="text-center py-12" data-testid="dashboard-empty">
      <Activity className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
      <p className="text-zinc-500 text-sm">No data yet. Start logging your protocol to see insights.</p>
    </div>
  );

  const peptideData = Object.entries(data.peptide_doses || {}).map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-8" data-testid="dashboard-tab">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">Period:</span>
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1 text-xs font-mono ${days === d ? 'bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30' : 'text-zinc-500 border-zinc-800 hover:text-white'} border transition`}>{d}d</button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Doses', value: data.total_logs, icon: Syringe, color: '#00F0FF' },
          { label: 'Days Logged', value: data.days_logged, icon: CalendarDays, color: '#22c55e' },
          { label: 'Active Peptides', value: data.active_peptides?.length || 0, icon: Activity, color: '#a855f7' },
          { label: 'Side Effects', value: data.total_side_effects, icon: AlertTriangle, color: '#f59e0b' },
        ].map(card => (
          <div key={card.label} className="p-4 bg-zinc-900/30 border border-zinc-800/50" data-testid={`dashboard-${card.label.toLowerCase().replace(/ /g,'-')}`}>
            <card.icon className="w-4 h-4 mb-2" style={{ color: card.color }} />
            <p className="text-2xl font-heading text-white">{card.value}</p>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Streaks & Achievements */}
      <StreaksAndAchievements token={token} />

      {/* Dose Distribution Chart */}
      {peptideData.length > 0 && (
        <div>
          <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Dose Distribution</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={peptideData} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis type="number" tick={{ fill: '#71717a', fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 11 }} width={75} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', color: '#fff', fontSize: 12 }} />
              <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                {peptideData.map((entry, i) => <Cell key={entry.name} fill={['#00F0FF', '#a855f7', '#22c55e', '#f59e0b', '#ef4444'][i % 5]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Daily Activity */}
      {(data.daily_counts || []).length > 0 && (
        <div>
          <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Daily Activity</h4>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={data.daily_counts}>
              <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 9 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fill: '#71717a', fontSize: 10 }} width={20} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', color: '#fff', fontSize: 12 }} />
              <Bar dataKey="count" fill="#00F0FF" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Side Effect Summary */}
      {(data.symptom_summary || []).length > 0 && (
        <div>
          <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Side Effect Summary</h4>
          <div className="space-y-2">
            {data.symptom_summary.map(s => (
              <div key={s.symptom} className="flex items-center justify-between p-3 bg-zinc-900/30 border border-zinc-800/50">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-white text-sm">{s.symptom}</span>
                  <span className="text-zinc-600 text-xs">({s.peptides.join(', ')})</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-zinc-500 text-xs">{s.count}x</span>
                  <span className="text-xs px-2 py-0.5 border" style={{
                    color: SEVERITY_COLORS[Math.round(s.avg_severity)] || '#888',
                    borderColor: (SEVERITY_COLORS[Math.round(s.avg_severity)] || '#888') + '40'
                  }}>avg {s.avg_severity}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights */}
      <AIInsightsPanel token={token} />

      {/* Weekly Email Subscription */}
      <WeeklyEmailCard token={token} />
    </div>
  );
}

function WeeklyEmailCard({ token }) {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  useEffect(() => {
    apiClient.get('/protocol/email-preferences', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSubscribed(r.data?.weekly_summary || false))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const toggleSubscription = async () => {
    const newVal = !subscribed;
    setSubscribed(newVal);
    try {
      await apiClient.put('/protocol/email-preferences', { weekly_summary: newVal }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { setSubscribed(!newVal); }
  };

  const handleSendNow = async () => {
    setSending(true);
    setSent(false);
    try {
      const res = await apiClient.post('/protocol/weekly-email/send-now', {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.sent) setSent(true);
    } catch (e) { console.warn('Weekly email send failed:', e); }
    setSending(false);
  };

  const handlePreview = async () => {
    if (previewHtml) { setPreviewHtml(null); return; }
    try {
      const res = await apiClient.post('/protocol/weekly-email/preview', {}, { headers: { Authorization: `Bearer ${token}` } });
      setPreviewHtml(res.data?.preview || null);
    } catch (e) { console.warn('Weekly email preview failed:', e); }
  };

  if (loading) return null;

  return (
    <div data-testid="weekly-email-card">
      <div className="flex items-center justify-between p-4 bg-zinc-900/30 border border-zinc-800/50">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-[#00F0FF]" />
          <div>
            <p className="text-white text-sm font-medium">Weekly Protocol Summary</p>
            <p className="text-zinc-500 text-xs">AI-powered email every Monday with your week's adherence, side effects, and insights</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleSubscription}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition ${subscribed ? 'bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-[#00F0FF]/30'}`}
            data-testid="weekly-email-toggle">
            {subscribed ? 'Subscribed' : 'Subscribe'}
          </button>
        </div>
      </div>

      {subscribed && (
        <div className="flex items-center gap-2 p-3 border border-t-0 border-zinc-800/50 bg-zinc-900/10">
          <button onClick={handlePreview}
            className="text-[10px] text-zinc-500 hover:text-[#00F0FF] transition uppercase tracking-wider"
            data-testid="weekly-email-preview-btn">
            {previewHtml ? 'Hide Preview' : 'Preview Email'}
          </button>
          <span className="text-zinc-800">|</span>
          <button onClick={handleSendNow} disabled={sending}
            className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-[#00F0FF] transition uppercase tracking-wider disabled:opacity-40"
            data-testid="weekly-email-send-now">
            <Send className="w-2.5 h-2.5" />
            {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
            {sending ? 'Sending...' : sent ? 'Sent!' : 'Send Now'}
          </button>
        </div>
      )}

      {previewHtml && (
        <div className="border border-t-0 border-zinc-800/50 overflow-hidden" data-testid="weekly-email-preview">
          <div className="p-2 bg-zinc-900/50">
            <span className="text-[9px] text-zinc-600 uppercase tracking-wider">Email Preview</span>
          </div>
          <iframe
            srcDoc={previewHtml}
            title="Email Preview"
            className="w-full border-0 bg-[#0a0a0a]"
            style={{ height: '480px' }}
            sandbox=""
          />
        </div>
      )}
    </div>
  );
}

function CommunityTab({ token }) {
  const [stats, setStats] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [statsRes, sharingRes] = await Promise.all([
          apiClient.get('/protocol/community-stats'),
          token ? apiClient.get('/protocol/sharing', { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: {} }),
        ]);
        setStats(statsRes.data);
        setSharing(sharingRes.data?.sharing_enabled || false);
      } catch (e) { console.warn('Protocol stats fetch failed:', e); }
      setLoading(false);
    };
    fetchAll();
  }, [token]);

  const toggleSharing = async () => {
    try {
      const res = await apiClient.put('/protocol/sharing', { sharing_enabled: !sharing }, { headers: { Authorization: `Bearer ${token}` } });
      setSharing(res.data.sharing_enabled);
    } catch (e) { console.warn('Sharing toggle failed:', e); }
  };

  if (loading) return <div className="h-40 bg-zinc-900/50 animate-pulse" />;

  return (
    <div className="space-y-8" data-testid="community-tab">
      {/* Sharing toggle */}
      {token && (
        <div className="flex items-center justify-between p-4 bg-zinc-900/30 border border-zinc-800/50" data-testid="sharing-toggle-card">
          <div className="flex items-center gap-3">
            {sharing ? <Eye className="w-5 h-5 text-emerald-400" /> : <EyeOff className="w-5 h-5 text-zinc-600" />}
            <div>
              <p className="text-white text-sm font-medium">Anonymous Data Sharing</p>
              <p className="text-zinc-500 text-xs">Contribute your anonymized protocol data to help the community</p>
            </div>
          </div>
          <button onClick={toggleSharing}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition ${sharing ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-emerald-500/30'}`}
            data-testid="sharing-toggle-btn">
            {sharing ? 'Sharing On' : 'Enable'}
          </button>
        </div>
      )}

      {/* Community Stats */}
      {stats && stats.total_contributors > 0 ? (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-4 bg-zinc-900/30 border border-zinc-800/50 text-center">
              <p className="text-2xl font-heading text-[#00F0FF]">{stats.total_contributors}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Contributors</p>
            </div>
            <div className="p-4 bg-zinc-900/30 border border-zinc-800/50 text-center">
              <p className="text-2xl font-heading text-white">{stats.total_logs}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Total Doses Logged</p>
            </div>
            <div className="p-4 bg-zinc-900/30 border border-zinc-800/50 text-center">
              <p className="text-2xl font-heading text-amber-400">{stats.total_side_effect_reports}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Effect Reports</p>
            </div>
          </div>

          {(stats.peptide_popularity || []).length > 0 && (
            <div>
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Most Tracked Peptides</h4>
              <div className="space-y-1.5">
                {stats.peptide_popularity.map((p, i) => (
                  <div key={p.peptide} className="flex items-center justify-between p-3 bg-zinc-900/30 border border-zinc-800/50">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-600 font-mono w-5">#{i + 1}</span>
                      <span className="text-white text-sm">{p.peptide}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-zinc-500">
                      <span>{p.users} users</span>
                      <span>{p.total_doses} doses</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(stats.side_effect_rates || []).length > 0 && (
            <div>
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Community-Reported Side Effects</h4>
              <div className="space-y-1.5">
                {stats.side_effect_rates.slice(0, 10).map(se => (
                  <div key={`${se.peptide}-${se.symptom}`} className="flex items-center justify-between p-3 bg-zinc-900/30 border border-zinc-800/50">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-white text-sm">{se.symptom}</span>
                      <span className="text-zinc-600 text-xs">({se.peptide})</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-zinc-500">{se.reports} reports</span>
                      <span style={{ color: SEVERITY_COLORS[Math.round(se.avg_severity)] || '#888' }}>
                        avg {se.avg_severity}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm mb-2">No community data yet</p>
          <p className="text-zinc-600 text-xs max-w-md mx-auto">
            Be the first to contribute! Enable anonymous sharing above to help build the world's largest real-world peptide protocol database.
          </p>
        </div>
      )}

      <div className="p-4 bg-[#00F0FF]/5 border border-[#00F0FF]/10 text-xs text-zinc-500">
        <Shield className="w-4 h-4 text-[#00F0FF] inline mr-2" />
        All community data is fully anonymized. No personal information, email, or identity is ever shared. Only aggregate peptide, dose, and side effect data is included.
      </div>
    </div>
  );
}

export default function ProtocolTrackerPage() {
  const { token, user, loading } = useAuth();
  const isLoggedIn = !!token && !!user;
  const [activeTab, setActiveTab] = useState('log');
  const [options, setOptions] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    apiClient.get('/protocol/options').then(r => setOptions(r.data)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  if (!isLoggedIn) {
    return (
      <main className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="protocol-tracker-login-required">
        <div className="text-center max-w-md">
          <Syringe className="w-12 h-12 text-[#00F0FF] mx-auto mb-4" />
          <h1 className="text-3xl font-heading text-white mb-3">Protocol Tracker</h1>
          <p className="text-zinc-500 text-sm mb-6">Log in to track your peptide protocol, monitor side effects, and access your personal dashboard.</p>
          <Link to="/auth" className="inline-block px-6 py-2.5 bg-[#00F0FF] text-black font-bold text-sm uppercase tracking-wider hover:bg-[#00d6e0] transition" data-testid="login-cta">
            Log In to Start
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0a0a0a]" data-testid="protocol-tracker-page">
      {/* Header */}
      <section className="border-b border-zinc-800/50 py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#00F0FF]/10 flex items-center justify-center">
              <Syringe className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading text-white">PROTOCOL TRACKER</h1>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Log doses &middot; Track side effects &middot; Share insights</p>
            </div>
          </div>
          <p className="text-sm text-zinc-500 mt-3 max-w-2xl">
            Track your peptide protocol with daily dose logs, side effect monitoring, and personal analytics.
            Contribute anonymized data to build the community's most comprehensive real-world peptide database.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b border-zinc-800/50 px-4 sticky top-0 bg-[#0a0a0a] z-30">
        <div className="max-w-4xl mx-auto flex gap-0 overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-xs uppercase tracking-wider border-b-2 transition whitespace-nowrap ${activeTab === tab.id ? 'text-[#00F0FF] border-[#00F0FF]' : 'text-zinc-600 border-transparent hover:text-zinc-400'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {activeTab === 'log' && <LogDoseTab token={token} options={options} onRefresh={() => setRefreshKey(k => k + 1)} />}
        {activeTab === 'timeline' && <TimelineTab token={token} key={`timeline-${refreshKey}`} />}
        {activeTab === 'dashboard' && <DashboardTab token={token} key={`dashboard-${refreshKey}`} />}
        {activeTab === 'community' && <CommunityTab token={token} />}
      </div>

      {/* Related Article CTA */}
      <section className="border-t border-zinc-800/50 py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/blogs/article/peptide-side-effects-what-the-research-actually-says"
            className="group flex items-center justify-between p-5 bg-zinc-900/30 border border-zinc-800/40 hover:border-[#00F0FF]/30 transition"
            data-testid="related-article-cta">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <p className="text-white text-sm font-medium group-hover:text-[#00F0FF] transition">Peptide Side Effects: What the Research Actually Says</p>
                <p className="text-zinc-600 text-xs">Evidence-based guide to BPC-157, TB-500, GHK-Cu, CJC-1295 + Ipamorelin & AOD-9604</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#00F0FF] opacity-0 group-hover:opacity-100 transition flex-shrink-0" />
          </Link>
        </div>
      </section>
    </main>
  );
}
